import React, { useMemo, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import Loader from "../../../components/Loader";
import NoData from "../../../components/NoData";
import { MdClose } from "react-icons/md";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import PlaceBetServices from "../../../services/PlaceBetServices";
import TransactionHistoryServices from "../../../services/TransactionHistoryServices";

/** Remove YT_PLACEBET_ prefix for display/print */
const stripPlaceBetUidPrefix = (val) => {
  if (val == null || val === "") return "—";
  const s = String(val).trim();
  return s.replace(/^YT_PLACEBET_/i, "") || s || "—";
};

const fmtMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

const extractCodeHint = (row) => {
  const direct = row?.code_hint;
  if (direct && String(direct).trim()) return String(direct).trim();
  const remark = String(row?.remark || "");
  if (!remark) return null;

  // WIN remark examples:
  // - "Session win credit (...) by bar code 123456."
  // - "Session win credit (...) by bar code: 123456"
  const winMatch = remark.match(/bar\s*code\s*[:#-]?\s*([0-9]{4,})/i);
  if (winMatch?.[1]) return winMatch[1];

  // CANCEL remark example:
  // - "Cancel place bet slip=123456" OR slip=YT_PLACEBET_....
  const slipMatch = remark.match(/slip\s*=\s*([A-Za-z0-9_-]{4,})/i);
  if (slipMatch?.[1]) return slipMatch[1];

  return null;
};

const MobileTransactionHistory = (props) => {
  const { rows, loading, page, pagination, limit, setLimit, setPage } = props;
  const api = new PlaceBetServices();
  const txApi = new TransactionHistoryServices();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMode, setDetailMode] = useState("purchase"); // purchase | win
  const [detail, setDetail] = useState(null);

  const cards = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r) => {
        const refType = String(r?.reference_type || "").toUpperCase();
        const txnType = String(r?.txn_type || "").toUpperCase();
        const amount = Number(r?.amount || 0);
        const opening = Number(r?.balance_before || 0);
        const closing = Number(r?.balance_after || 0);
        const signedAmount = txnType === "DEBIT" ? -Math.abs(amount) : amount;

        let kind = "OTHER";
        if (refType === "PLACE_BET") kind = "PURCHASE";
        else if (refType === "GAME_WIN") kind = "WIN";
        else if (refType === "CANCEL_BET") kind = "CANCEL";

        return {
          id: r?.place_bet_id || null, // slip id (best-effort mapping)
          wallet_txn_id: r?.wallet_txn_id || r?.id || null,
          created_at: r?.created_at || null, // wallet txn timestamp
          placebet_uid: r?.placebet_uid || null,
          bar_code: r?.bar_code || null,
          draw_time: r?.draw_time || null,
          game_type: r?.game_type || null,
          code_hint: extractCodeHint(r),
          kind,
          opening,
          amount: signedAmount,
          closing,
          remark: r?.remark || "",
          reference_type: r?.reference_type || "",
          txn_type: r?.txn_type || "",
        };
      })
      .filter((c) => c.kind !== "OTHER");
  }, [rows]);

  const openDetail = async (card) => {
    // Primary: open by mapped place_bet_id
    // Fallback: use ledger code_hint (barcode / uid) to resolve place bet id safely.
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setDetailMode(card.kind === "WIN" ? "win" : "purchase");
    try {
      let placeBetId = card?.id ? Number(card.id) : null;
      if (!placeBetId && card?.code_hint) {
        const lookup = await api.getPlaceBetIdByCode(card.code_hint);
        if (lookup?.isSuccess && lookup?.data?.id) {
          placeBetId = Number(lookup.data.id);
        }
      }
      if (placeBetId) {
        const res = await api.getPlaceBetById(placeBetId);
        if (res?.isSuccess && res?.data) {
          setDetail(res.data);
          return;
        }
      }

      // WIN fallback: session-level win credit (settlement) may not map to a single slip.
      if (card?.kind === "WIN" && card?.wallet_txn_id) {
        const winRes = await txApi.getMobileWinDetail(card.wallet_txn_id);
        if (winRes?.isSuccess && winRes?.data) {
          setDetail(winRes.data);
          return;
        }
      }

      toast.error("Detail not available for this entry");
      setDetailOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load detail");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailLoading(false);
    setDetail(null);
  };

  const downloadReceiptPdf = () => {
    if (!detail) return;
    const pb = detail?.place_bet_data || {};
    const yantras = Array.isArray(pb?.yantra_list)
      ? pb.yantra_list.filter((y) => Number(y?.yantra_count) > 0)
      : [];
    const session = detail?.session || {};
    const doc = new jsPDF({ orientation: "portrait", unit: "mm" });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 14;

    const title =
      detailMode === "win"
        ? "Yantra Winner Receipt"
        : "Yantra Purchase Receipt";
    doc.setFontSize(14);
    doc.text(title, pageWidth / 2, y, { align: "center" });
    y += 7;

    doc.setFontSize(10);
    const uid = stripPlaceBetUidPrefix(pb?.placebet_uid || "");
    const createdAt = pb?.created_at
      ? moment.utc(pb.created_at).local().format("DD-MM-YYYY hh:mm A")
      : "—";
    const drawTime = pb?.draw_time || "—";

    if (detailMode === "win") {
      const gameId = detail?.user?.user_game_id || "—";
      const idName =
        detail?.user?.user_username ||
        detail?.user?.user_name ||
        detail?.user?.user_game_id ||
        "—";
      const purchaseTime = createdAt;
      const start = session?.start_time
        ? moment.utc(session.start_time).local().format("hh:mm")
        : null;
      const end = session?.end_time
        ? moment.utc(session.end_time).local().format("hh:mm A")
        : null;
      const drawRange = start && end ? `${start}-${end}` : drawTime;

      const winningIds = Array.isArray(detail?.winning_yantra_ids)
        ? detail.winning_yantra_ids.map((n) => Number(n))
        : [];
      const winRows = yantras
        .filter((y) => winningIds.includes(Number(y?.yantra_id)))
        .map((y) => {
          const qty = Number(y?.yantra_count || 0);
          const name = y?.yantra_name || `Yantra ${y?.yantra_id || ""}`;
          return { name, qty };
        })
        .filter((r) => r.qty > 0);

      doc.setFontSize(12);
      doc.text("Congratulations!!", pageWidth / 2, y, { align: "center" });
      y += 7;

      doc.setFontSize(10);
      doc.text(`Yantra ID: ${gameId}`, 14, y);
      doc.text(`ID/Name: ${idName}`, 14, y + 5);
      doc.text(`Draw Time: ${drawRange}`, 14, y + 10);
      doc.text(`Purchase Time: ${purchaseTime}`, 14, y + 15);
      y += 22;

      autoTable(doc, {
        startY: y,
        head: [["#", "Yantra Purchase"]],
        body:
          winRows.length > 0
            ? winRows.map((r, idx) => [
                idx + 1,
                `${r.name} x ${r.qty} = ${r.qty} Silver Coin`,
              ])
            : [["1", "—"]],
        theme: "grid",
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 },
      });
      const fileName = `winner_receipt_${uid || pb?.id || "receipt"}.pdf`;
      doc.save(fileName);
      return;
    }

    doc.text(`Receipt No: ${uid}`, 14, y);
    doc.text(`Purchase Time: ${createdAt}`, 14, y + 5);
    doc.text(`Draw Time: ${drawTime}`, 14, y + 10);
    y += 16;

    autoTable(doc, {
      startY: y,
      head: [["Yantra", "Qty", "Price", "Amount"]],
      body: yantras.map((r) => [
        r?.yantra_name || `Yantra ${r?.yantra_id}`,
        Number(r?.yantra_count || 0),
        Number(r?.yantra_amount || 0),
        Number(r?.yantra_count || 0) * Number(r?.yantra_amount || 0),
      ]),
      theme: "grid",
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 },
    });

    const fileName = `purchase_receipt_${uid || pb?.id || "receipt"}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="mth-wrap">
      {loading ? (
        <Loader message="Loading transactions..." />
      ) : !cards.length ? (
        <NoData
          message="No Transaction History Found"
          subText="Try changing date range or search keyword."
        />
      ) : (
        <>
          <div className="mth-list">
            {cards.map((c, idx) => {
              const isWin = c.kind === "WIN";
              const isCancel = c.kind === "CANCEL";
              const dt = c.created_at
                ? moment
                    .utc(c.created_at)
                    .local()
                    .format("DD-MM-YYYY hh:mm:ss A")
                : "—";
              const amountAbs = Math.abs(Number(c.amount || 0));
              return (
                <div
                  key={`${c.kind}-${c.wallet_txn_id || c.id || idx}`}
                  className={`mth-card ${isWin || isCancel ? "win" : "purchase"}`}
                >
                  <div className="mth-date">{dt}</div>
                  <div
                    className={`mth-message ${isWin || isCancel ? "win" : "purchase"}`}
                  >
                    {isWin
                      ? `You have won ₹${fmtMoney(amountAbs)} in Yantra Purchase.`
                      : isCancel
                        ? `Bet cancelled. Refund ₹${fmtMoney(amountAbs)} in Yantra Purchase.`
                        : `You have purchased ₹${fmtMoney(amountAbs)} in Yantra Purchase.`}
                  </div>
                  <div className="mth-divider" />

                  <div className="mth-rows">
                    <div className="mth-row">
                      <span>Opening Balance</span>
                      <span>{fmtMoney(c.opening)}</span>
                    </div>
                    <div className="mth-row">
                      <span>Amount</span>
                      <span
                        className={`mth-amount ${isWin || isCancel ? "plus" : "minus"}`}
                      >
                        {isWin || isCancel ? "+" : "-"}
                        {fmtMoney(amountAbs)}
                      </span>
                    </div>
                    <div className="mth-dots" />
                    <div className="mth-row">
                      <span>Closing Balance</span>
                      <span>{fmtMoney(c.closing)}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="mth-viewmore"
                    onClick={() => openDetail(c)}
                    // disabled={!c.id && !c.code_hint}
                  >
                    View More
                  </button>
                </div>
              );
            })}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="modern-pagination">
              <div className="mp-limit">
                <span>Show</span>
                <select
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className="mp-controls">
                <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                  Prev
                </button>
                <div className="mp-info">
                  Page <strong>{pagination.page}</strong> of{" "}
                  <strong>{pagination.totalPages}</strong>
                </div>
                <button
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {detailOpen && (
        <div className="mth-modal-overlay" onClick={closeDetail}>
          <div className="mth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mth-modal-header">
              <div className="mth-modal-title">
                {detailMode === "win"
                  ? "Yantra Winner Receipt"
                  : "Yantra Purchase Receipt"}
              </div>
              <button type="button" className="mth-close" onClick={closeDetail}>
                <MdClose size={18} />
              </button>
            </div>
            <div className="mth-modal-body">
              {detailLoading ? (
                <Loader message="Loading..." />
              ) : detail ? (
                <>
                  {(() => {
                    const pb = detail?.place_bet_data || {};
                    const userName =
                      detail?.user?.user_name ||
                      detail?.user?.user_username ||
                      "—";
                    const gameId = detail?.user?.user_game_id || "—";
                    const createdAtLabel = pb.created_at
                      ? moment
                          .utc(pb.created_at)
                          .local()
                          .format("DD-MM-YYYY hh:mm A")
                      : "—";
                      const start1 = pb?.start_time
                        ? moment.utc(pb.start_time).local().format("hh:mm")
                        : null;
                      const end1 = pb?.end_time
                        ? moment.utc(pb.end_time).local().format("hh:mm A")
                        : null;
                    const drawTime = start1 && end1 ? `${start1}-${end1}` : pb.draw_time;
                    const uid = stripPlaceBetUidPrefix(
                      pb.placebet_uid || detail?.placebet_uid || "",
                    );
                    const totalBet = Number(pb.total_bet || 0);
                    const totalAmt = Number(pb.amount || 0);
                    const winAmt = Number(
                      detail?.winning_amount ?? pb?.win_amount ?? 0,
                    );
                    const yantras = Array.isArray(pb?.yantra_list)
                      ? pb.yantra_list.filter(
                          (y) => Number(y?.yantra_count) > 0,
                        )
                      : [];
                    const winningIds = Array.isArray(detail?.winning_yantra_ids)
                      ? detail.winning_yantra_ids.map((n) => Number(n))
                      : [];
                    if (detailMode === "win") {
                      const session = detail?.session || {};
                      const start2 = session?.start_time
                        ? moment.utc(session.start_time).local().format("hh:mm")
                        : null;
                      const end2 = session?.end_time
                        ? moment.utc(session.end_time).local().format("hh:mm A")
                        : null;
                      const drawRange =
                        start2 && end2 ? `${start2}-${end2}` : drawTime;
                      const idName =
                        detail?.user?.user_username ||
                        detail?.user?.user_name ||
                        detail?.user?.user_game_id ||
                        "—";

                      const winItems = yantras
                        .filter((y) =>
                          winningIds.includes(Number(y?.yantra_id)),
                        )
                        .map((y) => ({
                          yantra_id: y?.yantra_id,
                          yantra_name:
                            y?.yantra_name || `Yantra ${y?.yantra_id || ""}`,
                          qty: Number(y?.yantra_count || 0),
                        }))
                        .filter((x) => x.qty > 0);

                      const firstWinner =
                        winItems[0]?.yantra_name || "Winning Draw";

                      return (
                        <div className="mth-win-only">
                          <div className="mth-win-topline">
                            <div>Winning Draw</div>
                            <div className="mth-win-topline-right">
                              {firstWinner}
                            </div>
                          </div>

                          <div className="mth-win-congrats">
                            Congratulations!!
                          </div>

                          <div className="mth-receipt-top">
                            <div className="mth-receipt-title">
                              Yantra Session[{pb.game_type || "-"}]
                            </div>
                            <div className="mth-receipt-sub">
                              Receipt No: {uid}
                            </div>
                            <div className="mth-receipt-sub">
                              User: {userName} [{gameId}]
                            </div>
                            <div className="mth-receipt-sub muted">
                              Purchase Time: {createdAtLabel}
                            </div>
                            <div className="mth-receipt-sub muted">
                              Draw Time: {drawRange}
                            </div>
                          </div>

                          <div className="mth-win-table-wrap">
                            <table className="mth-win-table">
                              <thead>
                                <tr>
                                  <th className="center">#</th>
                                  <th>Yantra Purchase</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(winItems.length
                                  ? winItems
                                  : [{ yantra_name: "—", qty: 0 }]
                                ).map((w, idx) => (
                                  <tr key={`${w.yantra_id || "w"}-${idx}`}>
                                    <td className="center">{idx + 1}</td>
                                    <td>
                                      {w.yantra_name}
                                      {w.qty > 0
                                        ? ` x ${w.qty} = ${w.qty} Silver Coin`
                                        : ""}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <button
                            type="button"
                            className="mth-download"
                            onClick={downloadReceiptPdf}
                          >
                            Download Receipt
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className="mth-receipt">
                        <div className="mth-receipt-top">
                          <div className="mth-receipt-title">
                            Yantra Session[{pb.game_type || "-"}]
                          </div>
                          <div className="mth-receipt-sub">
                            Receipt No: {uid}
                          </div>
                          <div className="mth-receipt-sub">
                            User: {userName} [{gameId}]
                          </div>
                          <div className="mth-receipt-sub muted">
                            Purchase Time: {createdAtLabel}
                          </div>
                          <div className="mth-receipt-sub muted">
                            Draw Time: {drawTime}
                          </div>
                        </div>

                        <div className="mth-receipt-table-wrap">
                          <table className="mth-receipt-table">
                            <thead>
                              <tr>
                                <th>Yantra</th>
                                <th className="center">Qty</th>
                                <th className="center">Base Price</th>
                                <th className="right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {yantras.map((y, i) => {
                                const qty = Number(y?.yantra_count || 0);
                                const price = Number(y?.yantra_amount || 0);
                                const rowTotal = qty * price;
                                return (
                                  <tr key={`${y?.yantra_id || i}`}>
                                    <td>
                                      {y?.yantra_name ||
                                        `Yantra ${y?.yantra_id || ""}`}
                                    </td>
                                    <td className="center">{qty}</td>
                                    <td className="center">
                                      {fmtMoney(price)}
                                    </td>
                                    <td className="right">
                                      {fmtMoney(rowTotal)}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr className="mth-total-row">
                                <td>Total</td>
                                <td className="center">{totalBet}</td>
                                <td className="center" />
                                <td className="right">{fmtMoney(totalAmt)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <button
                          type="button"
                          className="mth-download"
                          onClick={downloadReceiptPdf}
                        >
                          Download Receipt
                        </button>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <NoData message="No detail available" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileTransactionHistory;
